import React from 'react';
import { Helmet } from 'react-helmet';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaChartLine, FaFileInvoiceDollar, FaExclamationTriangle, FaLinkedin } from 'react-icons/fa';
import { motion } from 'framer-motion';
import '../../styles/theme.css';
import img_1 from '../../assets/images/img_1.webp';
import img_3 from '../../assets/images/img_3.jpg';
import jayeshImage from '../../assets/images/team/jayesh-navalbhai-gadhvi.jpg';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer, staggerItem, getAnimationProps } from '../../utils/animations';

// AboutHighlights component and highlights array merged from AboutHighlights.js

const highlights = [
  "End-to-end RCM expertise under one roof",
  "HIPAA-compliant workflows and secure data handling",
  "Dedicated account managers for seamless coordination",
  "Cost-effective solutions with faster turnaround times",
];

function AboutHighlights() {
  return (
    <ul className="about-highlights">
      {highlights.map((item, index) => (
        <li key={index}>
          <CheckCircle className="highlight-icon" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const promises = [
  "Accurate Billing",
  "Faster Payments",
  "Fewer Denials",
  "Transparent Reporting",
  "24×7 Support",
  "100% HIPAA-Compliant Workflows",
];

function CorePromise() {
  return (
    <section className="core-promise">
      <motion.div {...getAnimationProps(fadeInUp)}>
        <h2 className="position-relative d-inline-block" style={{ paddingBottom: '40px' }}>
          Our Core Promise
          <span className="about-sub-underline" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '6px' }}></span>
        </h2>
        <p className="subtitle">
          Accuracy, compliance, and transparency you can rely on.
        </p>
      </motion.div>
      <motion.div 
        className="promise-grid"
        {...getAnimationProps(staggerContainer)}
      >
        {promises.map((item, index) => (
          <motion.div 
            className="promise-card" 
            key={index}
            variants={staggerItem}
          >
            <div className="icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle color="#2EB8A8" size={32} strokeWidth={2.5} />
            </div>
            <p>{item}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

const services = [
  {
    icon: <i className="bi bi-calendar-check" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}></i>,
    title: "Patient Scheduling & Demographics Entry",
    desc: "Accurate patient intake and insurance verification to prevent billing delays.",
  },
  {
    icon: <i className="bi bi-clipboard2-pulse" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}></i>,
    title: "Medical Coding & Charge Entry",
    desc: "Certified medical coding to ensure compliance and maximum reimbursement.",
  },
  {
    icon: <i className="bi bi-file-earmark-text" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}></i>,
    title: "Claims Submission & Billing",
    desc: "Clean and timely claims submission to reduce denials and speed up payments.",
  },
  {
    icon: <i className="bi bi-cash-stack" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}></i>,
    title: "Payment Posting",
    desc: "Accurate posting and reconciliation for complete financial transparency.",
  },
  {
    icon: <i className="bi bi-graph-up-arrow" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}></i>,
    title: "Accounts Receivable (A/R) Management",
    desc: "Proactive A/R follow-ups to accelerate collections and reduce outstanding balances.",
  },
  {
    icon: <i className="bi bi-exclamation-octagon" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}></i>,
    title: "Denial Management & Claim Appeals",
    desc: "Root-cause analysis and appeals management to recover lost revenue.",
  }
];

const analyticsSummary = [
  {
    icon: <FaChartLine />,
    title: "Revenue Performance",
    desc: "Track charges, payments, and collection ratios to monitor financial health.",
  },
  {
    icon: <FaFileInvoiceDollar />,
    title: "Accounts Receivable (A/R)",
    desc: "Analyze outstanding balances and follow-ups for faster collections.",
  },
  {
    icon: <FaExclamationTriangle />,
    title: "Claims & Denials",
    desc: "Identify denial trends and root causes to improve acceptance rates.",
  }
];

// Team members for Home page (3 key people)
const teamHighlights = [
  {
    name: "Jayesh Navalbhai Gadhvi",
    title: "Founder & CEO",
    expertise: "5+ years in Healthcare Revenue Cycle Management",
    image: jayeshImage,
    linkedin: "https://www.linkedin.com/in/jayesh-gadhvi-868196199/"
  },
];

function WhyChooseUs() {
  return (
    <section className="py-5" style={{ background: '#f9fbfd' }}>
      <div className="container">
        <motion.div className="text-center mb-5" {...getAnimationProps(fadeInUp)}>
          <h2 className="fw-bold text-primary position-relative d-inline-block" style={{ paddingBottom: '40px' }}>
            Why Choose OptiBill Analytics?
            <span className="about-sub-underline" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '6px' }}></span>
          </h2>
          <p className="text-muted mt-2 mx-auto" style={{ maxWidth: "850px" }}>
            Led by experienced healthcare billing professionals dedicated to maximizing your revenue.
          </p>
        </motion.div>
        <motion.div 
          className="row g-4 justify-content-center"
          {...getAnimationProps(staggerContainer)}
        >
          {teamHighlights.map((member, index) => (
            <motion.div 
              className="col-md-6 col-lg-4" 
              key={index}
              variants={staggerItem}
            >
              <div className="service-card h-100 text-center p-4" style={{ minHeight: '320px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '120px', height: '120px', borderRadius: '50%', margin: '0 auto 20px', overflow: 'hidden', boxShadow: '0 8px 20px rgba(43,66,120,0.15)', border: '3px solid #2EB8A8' }}>
                  <img src={member.image} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <h5 className="fw-bold" style={{ color: '#2B4278', marginBottom: '8px' }}>{member.name}</h5>
                <p className="text-muted small fw-semibold" style={{ marginBottom: '12px' }}>{member.title}</p>
                <p className="text-muted small" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>{member.expertise}</p>
                <a href={member.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#2EB8A8', marginTop: 'auto', paddingTop: '12px' }}>
                  <FaLinkedin size={24} />
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function AnalyticsSummary() {
  return (
    <section className="py-5">
      <div className="container">
        <motion.div className="text-center mb-5" {...getAnimationProps(fadeInUp)}>
          <h2 className="fw-bold text-primary position-relative d-inline-block" style={{ paddingBottom: '40px' }}>
            Reporting & Revenue Analytics
            <span className="about-sub-underline" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '6px' }}></span>
          </h2>
          <p className="text-muted mt-2 mx-auto" style={{ maxWidth: "850px" }}>
            Actionable insights into claims, payments, denials, and A/R—designed to help healthcare practices make smarter financial decisions.
          </p>
        </motion.div>
        <motion.div 
          className="row g-4"
          {...getAnimationProps(staggerContainer)}
        >
          {analyticsSummary.map((item, index) => (
            <motion.div 
              className="col-md-6 col-lg-4" 
              key={index}
              variants={staggerItem}
            >
              <div className="service-card h-100 text-center p-4">
                <div className="service-icon mb-3" style={{ fontSize: '2.2rem', color: '#2eb8a8' }}>{item.icon}</div>
                <h5 className="fw-semibold">{item.title}</h5>
                <p className="text-muted small mt-2">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <div className="text-center mt-5">
          <button className="about-btn" onClick={() => window.location.href = '/analytics'}>
            View Full Analytics Page
          </button>
        </div>
      </div>
    </section>
  );
}

function ServicesSummary() {
  return (
    <section className="py-5 bg-light">
      <div className="container">
        <motion.div className="text-center mb-5" {...getAnimationProps(fadeInUp)}>
          <h2 className="fw-bold text-primary position-relative d-inline-block" style={{ paddingBottom: '40px' }}>
            Our Medical Billing & RCM Services
            <span className="about-sub-underline" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '6px' }}></span>
          </h2>
          <p className="text-muted mt-2 mx-auto" style={{ maxWidth: "850px" }}>
            End-to-end revenue cycle management solutions designed to improve
            cash flow, reduce denials, and simplify billing operations for
            healthcare practices. Plus specialized services including Provider Credentialing, 
            PMS Software expertise, and Multi-Specialty billing support.
          </p>
        </motion.div>
        <motion.div 
          className="row g-4"
          {...getAnimationProps(staggerContainer)}
        >
          {services.map((service, index) => (
            <motion.div 
              className="col-md-6 col-lg-4" 
              key={index}
              variants={staggerItem}
            >
              <div className="service-card h-100 text-center p-4">
                <div className="service-icon mb-3">{service.icon}</div>
                <h5 className="fw-semibold">{service.title}</h5>
                <p className="text-muted small mt-2">{service.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <div className="text-center mt-5">
          <button className="about-btn" onClick={() => window.location.href = '/services'}>
            Explore All Services
          </button>
        </div>
      </div>
    </section>
  );
}

const Home = () => {
  const navigate = useNavigate();
  return (
    <>
      <Helmet>
        <title>OptiBill Analytics | Medical Billing & Revenue Cycle Management Services</title>
        <meta name="description" content="Professional medical billing and RCM services for healthcare providers. End-to-end revenue cycle management with HIPAA compliance, faster payments, and transparent reporting." />
        <meta name="keywords" content="medical billing services, revenue cycle management, RCM services, healthcare billing, insurance claim submission, denial management, accounts receivable, HIPAA compliant billing" />
        <meta property="og:title" content="OptiBill Analytics | Medical Billing & RCM Services" />
        <meta property="og:description" content="Trusted partner for end-to-end revenue cycle management. Maximize revenue, reduce denials, and streamline billing operations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.optibillanalytics.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OptiBill Analytics | Medical Billing & RCM Services" />
        <meta name="twitter:description" content="Professional medical billing and revenue cycle management services for healthcare providers." />
        <link rel="canonical" href="https://www.optibillanalytics.com" />
      </Helmet>
      <section className="hero-section">
        <img 
          src={img_1}
          alt="Hero" 
          className="hero-image"
        />
        <div className="hero-overlay">
          <div className="hero-teal-overlay"></div>
          <motion.div 
            className="hero-text-right"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
          >
            <div className="company-name">OptiBill Analytics</div>
            <span>Your Trusted Partner</span><br />
            <span>For End-to-End</span><br />
            <span>Revenue Cycle</span><br />
            <span>Management (RCM)</span>
            <div className="hero-subtext">
              We help healthcare providers maximize revenue, reduce denials, and streamline the entire revenue cycle - so you can focus on patient care.
            </div>
            <div className="hero-cta-group">
              <button className="hero-cta-btn" onClick={() => window.openContactPopup && window.openContactPopup()}>Request Free Consultation</button>
            </div>
          </motion.div>
        </div>
      </section>


      <CorePromise />

      {/* About OptiBill Analytics Section - Upgraded UI */}
      <section className="about-section">
        <div className="about-content">
          <motion.div className="about-text" {...getAnimationProps(fadeInLeft)}>
            <div className="about-heading-center">
              <h2>About OptiBill Analytics</h2>
              <div className="about-divider"></div>
            </div>
            <h4>Who We Are</h4>
            <div className="about-sub-underline"></div>
            <p>
              OptiBill Analytics is a professional Medical Billing and End-to-End Revenue Cycle Management (RCM) service provider, helping healthcare practices streamline billing operations, reduce denials, and improve cash flow.
            </p>
            <p>
              With a secure global delivery model and India-based operations, we support healthcare providers across the United States and international markets with accurate, compliant, and timely RCM services.
            </p>
            <h4>What Sets Us Apart</h4>
            <div className="about-sub-underline"></div>
            <AboutHighlights />
            <button className="about-btn" onClick={() => navigate('/about')}>Learn More About Us</button>
          </motion.div>
          <motion.div className="about-image" {...getAnimationProps(fadeInRight)}>
            <img src={img_3} alt="About OptiBill Analytics" />
          </motion.div>
        </div>
      </section>
      <ServicesSummary />
      <WhyChooseUs />
      <AnalyticsSummary />
    </>
  );
};

export default Home;
